import { Modal, Stack, TextInput, Textarea, Button, Group, Table, Checkbox, Alert, Text } from '@mantine/core';
import { useState, useEffect } from 'react';
import { IconShieldCheck, IconAlertCircle } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useOrganization } from '@/hooks/useOrganization';
import { useCreateCustomRole, useUpdateCustomRole, type CustomRole } from '@/hooks/api/useCustomRoles';
import type { UserPermissions, PagePermissions } from '@/hooks/usePermissions';

interface CustomRoleModalProps {
  opened: boolean;
  onClose: () => void;
  role: CustomRole | null;
}

const PAGES = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'assets', label: 'Assets' },
  { id: 'audits', label: 'Audits' },
  { id: 'reports', label: 'Reports' },
  { id: 'people', label: 'People' },
  { id: 'locations', label: 'Locations' },
  { id: 'categories', label: 'Categories' },
  { id: 'departments', label: 'Departments' },
  { id: 'custom_fields', label: 'Custom Fields' },
  { id: 'settings', label: 'Settings' },
  { id: 'billing', label: 'Billing & Subscription' },
];

const DEFAULT_PAGE_PERMISSIONS: PagePermissions = {
  view: false,
  write: false,
  delete: false,
};

export function CustomRoleModal({ opened, onClose, role }: CustomRoleModalProps) {
  const { organizationId } = useOrganization();
  const createRole = useCreateCustomRole();
  const updateRole = useUpdateCustomRole();

  const [permissions, setPermissions] = useState<UserPermissions>({});

  const form = useForm({
    initialValues: {
      name: '',
      description: '',
    },
    validate: {
      name: (value) => (!value.trim() ? 'Role name is required' : null),
    },
  });

  // Initialize form and permissions when role changes
  useEffect(() => {
    if (role) {
      form.setValues({
        name: role.name,
        description: role.description || '',
      });
      setPermissions(role.permissions);
    } else {
      form.reset();
      setPermissions({});
    }
  }, [role, opened]);

  const getPagePermissions = (pageId: string): PagePermissions => {
    return permissions[pageId] || DEFAULT_PAGE_PERMISSIONS;
  };

  const updatePagePermission = (pageId: string, field: keyof PagePermissions, value: boolean) => {
    setPermissions((prev) => ({
      ...prev,
      [pageId]: {
        ...getPagePermissions(pageId),
        [field]: value,
        // If disabling view, disable all other permissions
        ...(field === 'view' && !value ? {
          write: false,
          delete: false,
        } : {}),
      },
    }));
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!organizationId) return;

    try {
      if (role) {
        // Update existing role
        await updateRole.mutateAsync({
          id: role.id,
          name: values.name,
          description: values.description,
          permissions,
        });
      } else {
        // Create new role
        await createRole.mutateAsync({
          organizationId,
          name: values.name,
          description: values.description,
          permissions,
        });
      }

      onClose();
    } catch (error) {
      // Error handling is done in the mutation hooks
    }
  };

  const isLoading = createRole.isPending || updateRole.isPending;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconShieldCheck size={24} />
          <Text size="lg" fw={600}>
            {role ? 'Edit Custom Role' : 'Create Custom Role'}
          </Text>
        </Group>
      }
      size="xl"
      scrollAreaComponent={Modal.NativeScrollArea}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="blue">
            <Text size="sm">
              Define a reusable role template with specific permissions.
              You can then assign this role to multiple users from the Users page.
            </Text>
          </Alert>

          <TextInput
            label="Role Name"
            placeholder="e.g., Auditor, Viewer, Asset Manager"
            required
            {...form.getInputProps('name')}
          />

          <Textarea
            label="Description"
            placeholder="Describe what this role can do..."
            rows={2}
            {...form.getInputProps('description')}
          />

          <div>
            <Text size="sm" fw={500} mb="xs">
              Permissions
            </Text>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Page</Table.Th>
                  <Table.Th style={{ textAlign: 'center', width: '100px' }}>View</Table.Th>
                  <Table.Th style={{ textAlign: 'center', width: '120px' }}>Create/Edit</Table.Th>
                  <Table.Th style={{ textAlign: 'center', width: '100px' }}>Delete</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {PAGES.map((page) => {
                  const perms = getPagePermissions(page.id);
                  return (
                    <Table.Tr key={page.id}>
                      <Table.Td>
                        <Text fw={500}>{page.label}</Text>
                      </Table.Td>
                      <Table.Td>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Checkbox
                            checked={perms.view}
                            onChange={(e) => updatePagePermission(page.id, 'view', e.currentTarget.checked)}
                          />
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Checkbox
                            checked={perms.write}
                            disabled={!perms.view}
                            onChange={(e) => updatePagePermission(page.id, 'write', e.currentTarget.checked)}
                          />
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Checkbox
                            checked={perms.delete}
                            disabled={!perms.view}
                            onChange={(e) => updatePagePermission(page.id, 'delete', e.currentTarget.checked)}
                          />
                        </div>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              leftSection={<IconShieldCheck size={16} />}
            >
              {role ? 'Update Role' : 'Create Role'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
